import { Money, cents, showMoney } from './Money';

export interface FeeInfo {
    feeName: string;
    amount: number;
    feeCalc: number; // 0 = Fixed, 1 = Percentage
    feeScope: number; // 0 = Reservation, 1 = Subscription
    paymentType: number; // 0 = Credit Card, 1 = ACH
    isAppliedToParker: boolean;
}

export interface FeeCalculationResult {
    baseAmount: number;
    fees: FeeInfo[];
    totalAmount: number;
    facilityCharge: number;
    parkerCharge: number;
}

/**
 * Calculate fee amount based on calculation type and base amount
 * @param feeAmount - The fee amount (in cents for fixed, decimal for percentage)
 * @param feeCalc - 0 for fixed amount, 1 for percentage
 * @param baseAmount - Base amount to calculate percentage from (default $10)
 * @returns The calculated fee amount in cents
 */
export const calculateFeeAmount = (feeAmount: number, feeCalc: number, baseAmount: number = 1000): number => {
    if (feeCalc === 0) {
        // Fixed amount: feeAmount is already in cents
        return feeAmount;
    } else if (feeCalc === 1) {
        // Percentage: feeAmount is decimal (e.g., 0.15 for 15%)
        return Math.round(baseAmount * feeAmount);
    }
    return 0;
};

/**
 * Format fee amount for display
 * @param feeAmount - The fee amount
 * @param feeCalc - 0 for fixed amount, 1 for percentage
 * @returns Formatted string
 */
export const formatFeeAmount = (feeAmount: number, feeCalc: number): string => {
    if (feeCalc === 0) {
        // Fixed amount: display as currency
        return showMoney(cents(feeAmount));
    } else if (feeCalc === 1) {
        // Percentage: display as percentage
        return `${(feeAmount * 100).toFixed(1)}%`;
    }
    return 'N/A';
};

/**
 * Get fee scope text
 * @param scope - Fee scope value
 * @returns Human readable scope text
 */
export const getFeeScopeText = (scope: number): string => {
    switch (scope) {
        case 0: return "Reservation";
        case 1: return "Subscription";
        default: return "Unknown";
    }
};

/**
 * Get fee calculation type text
 * @param calc - Fee calculation type
 * @returns Human readable calculation type text
 */
export const getFeeCalcText = (calc: number): string => {
    console.log(calc);
    switch (calc) {          
        case 0: return "Fixed";
        case 1: return "Percent";
        default: return "Unknown";
    }
};

/**
 * Get payment type text
 * @param type - Payment type
 * @returns Human readable payment type text
 */
export const getPaymentTypeText = (type: number): string => {
    switch (type) {
        case 0: return "Credit Card";
        case 1: return "ACH";
        default: return "Unknown";
    }
};

/**
 * Calculate total fees and charges from fee list
 * @param fees - Array of fee information
 * @param baseAmount - Base amount in cents (default $10 = 1000 cents)
 * @returns Calculation result with totals
 */
export const calculateFeeTotals = (fees: FeeInfo[], baseAmount: number = 1000): FeeCalculationResult => {
    let totalFeeAmount = 0;
    let facilityCharge = 0;
    let parkerCharge = 0;

    fees.forEach(fee => {
        const calculatedAmount = calculateFeeAmount(fee.amount, fee.feeCalc, baseAmount);
        totalFeeAmount += calculatedAmount;

        if (fee.isAppliedToParker) {
            parkerCharge += calculatedAmount;
        } else {
            facilityCharge += calculatedAmount;
        }
    });

    return {
        baseAmount: baseAmount / 100, // Convert back to dollars for display
        fees,
        totalAmount: (baseAmount + totalFeeAmount) / 100, // Convert back to dollars for display
        facilityCharge: facilityCharge / 100, // Convert back to dollars for display
        parkerCharge: parkerCharge / 100 // Convert back to dollars for display
    };
}; 